import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { FloatingPortal } from "../Floating/FloatingPortal.jsx";
import { useAnchoredFloatLayout } from "../Floating/useAnchoredFloatLayout.js";
import { onKey } from "../../lib/shortcuts/onKey.js";
import { useEscapeStack } from "../../lib/shortcuts/useEscapeStack.js";
import { useShortcutBindings } from "../../lib/shortcuts/useShortcutBindings.js";

function normalizeOption(option) {
  if (typeof option === "string") {
    return {
      label: option,
      value: option
    };
  }

  return option;
}

function matchesOption(option, query) {
  return option.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
}

function findClosestOption(options, query) {
  const normalizedQuery = query.trim().toLocaleLowerCase();

  if (!normalizedQuery) {
    return options[0] ?? null;
  }

  return options.find((option) => option.value.toLocaleLowerCase() === normalizedQuery)
    ?? options.find((option) => option.label.toLocaleLowerCase() === normalizedQuery)
    ?? options.find((option) => option.label.toLocaleLowerCase().startsWith(normalizedQuery))
    ?? options.find((option) => option.label.toLocaleLowerCase().includes(normalizedQuery))
    ?? null;
}

export function DataGridComboboxEditor({
  column,
  inputRef,
  onCancel,
  onChange,
  onBlurCommit,
  onCommit,
  onTabBackward,
  onTabForward,
  row,
  value
}) {
  const widthBuffer = 16;
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const initialValueRef = useRef(value);
  const ignoreNextBlurRef = useRef(false);
  const [hasTyped, setHasTyped] = useState(false);
  const options = useMemo(() => {
    const resolvedOptions = typeof column.editor?.options === "function"
      ? column.editor.options(row)
      : column.editor?.options;

    return (resolvedOptions ?? []).map(normalizeOption);
  }, [column.editor, row]);
  const allowCustomValue = column.editor?.allowCustomValue ?? true;
  const maxVisibleOptions = Math.max(1, Number(column.editor?.maxVisibleOptions ?? 6));
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const handleAnchorInvalidated = useCallback(() => {
    ignoreNextBlurRef.current = true;
    onCancel();
  }, [onCancel]);
  const getScrollContainer = useCallback(
    (anchorElement) => anchorElement.closest('[data-role="data-grid"]'),
    []
  );
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value || option.label === value) ?? null,
    [options, value]
  );
  const inputValue = hasTyped ? value : (selectedOption?.label ?? value);
  const filteredOptions = useMemo(() => {
    if (!hasTyped || !value.trim()) {
      return options;
    }

    return options.filter((option) => matchesOption(option, value));
  }, [hasTyped, options, value]);

  useEffect(() => {
    if (!hasTyped) {
      const selectedIndex = options.findIndex((option) => option.value === value || option.label === value);
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
      return;
    }

    setHighlightedIndex(0);
  }, [hasTyped, options, value]);

  const rootFontSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
  const visibleOptionCount = Math.max(1, Math.min(filteredOptions.length, maxVisibleOptions));
  const preferredMenuHeight = visibleOptionCount * (2.55 * rootFontSize);
  const menuLayout = useAnchoredFloatLayout({
    anchorRef: wrapperRef,
    isOpen: filteredOptions.length > 0,
    preferredHeight: preferredMenuHeight,
    onAnchorInvalidated: handleAnchorInvalidated,
    getScrollContainer
  });

  useLayoutEffect(() => {
    const menuElement = menuRef.current;

    if (!menuElement || filteredOptions.length === 0) {
      return;
    }

    const activeOption = menuElement.querySelector(".combobox-option.active");
    activeOption?.scrollIntoView({
      block: "nearest"
    });
  }, [filteredOptions.length, highlightedIndex]);

  useEscapeStack(() => {
    ignoreNextBlurRef.current = true;
    onCancel();
    return true;
  });

  function commitValue(nextValue) {
    ignoreNextBlurRef.current = true;
    onChange(nextValue);
    onCommit(nextValue);
  }

  function resolveCommittedValue() {
    if (allowCustomValue) {
      return value;
    }

    const closestOption = findClosestOption(options, value);
    return closestOption?.value ?? initialValueRef.current;
  }

  const editorBindings = [
    onKey("ArrowDown", () => {
      if (filteredOptions.length === 0) {
        return;
      }

      setHighlightedIndex((current) => Math.min(current + 1, filteredOptions.length - 1));
    }, { allowInInput: true }),
    onKey("ArrowUp", () => {
      if (filteredOptions.length === 0) {
        return;
      }

      setHighlightedIndex((current) => Math.max(current - 1, 0));
    }, { allowInInput: true }),
    onKey("Enter", () => {
      const highlightedOption = filteredOptions[highlightedIndex];

      if (highlightedOption) {
        commitValue(highlightedOption.value);
        return;
      }

      commitValue(value);
    }, { allowInInput: true }),
    onKey("Tab", () => {
      const highlightedOption = filteredOptions[highlightedIndex];
      const nextValue = highlightedOption?.value ?? resolveCommittedValue();
      ignoreNextBlurRef.current = true;
      onChange(nextValue);
      onTabForward?.(nextValue);
    }, { allowInInput: true }),
    onKey("Shift+Tab", () => {
      const highlightedOption = filteredOptions[highlightedIndex];
      const nextValue = highlightedOption?.value ?? resolveCommittedValue();
      ignoreNextBlurRef.current = true;
      onChange(nextValue);
      onTabBackward?.(nextValue);
    }, { allowInInput: true })
  ];

  useShortcutBindings(wrapperRef, editorBindings);

  return (
    <div class="combobox-editor" ref={wrapperRef}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onInput={(event) => {
          setHasTyped(true);
          onChange(event.currentTarget.value);
        }}
        onBlur={() => {
          if (ignoreNextBlurRef.current) {
            ignoreNextBlurRef.current = false;
            return;
          }

          const resolvedValue = resolveCommittedValue();
          if (!onBlurCommit) {
            commitValue(resolvedValue);
            return;
          }

          onChange(resolvedValue);
          onBlurCommit(resolvedValue);
        }}
      />

      {filteredOptions.length > 0 && menuLayout ? (
        <FloatingPortal>
          <div
            class={`combobox-menu combobox-menu--floating${menuLayout.openUpward ? " combobox-menu--upward" : ""}`}
            ref={menuRef}
            style={{
              left: menuLayout.left === null ? "auto" : `${menuLayout.left}px`,
              right: menuLayout.right === null ? "auto" : `${menuLayout.right}px`,
              minWidth: `${menuLayout.width + widthBuffer}px`,
              width: "max-content",
              maxWidth: "min(calc(100vw - 1rem), 32rem)",
              maxHeight: `min(calc(${maxVisibleOptions} * 2.55rem), ${menuLayout.maxHeight}px)`,
              top: menuLayout.top === null ? "auto" : `${menuLayout.top}px`,
              bottom: menuLayout.bottom === null ? "auto" : `${menuLayout.bottom}px`
            }}
          >
            {filteredOptions.map((option, index) => (
              <div
                class={`combobox-option${index === highlightedIndex ? " active" : ""}`}
                role="option"
                aria-selected={index === highlightedIndex}
                key={option.value}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  commitValue(option.value);
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
        </FloatingPortal>
      ) : null}
    </div>
  );
}
